import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Building2, Users, UserCheck, RefreshCw, Copy, Check, TrendingUp, TrendingDown, Minus, Target, 
  Activity, ChevronDown, ChevronUp, LogOut, Settings, Calendar, Download, 
  Database, FileSpreadsheet, Table2, BarChart3, Trophy, UserPlus, Zap, Filter, Clock
} from "lucide-react";
import { toast } from "sonner";
import { 
  AreaChart, Area, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";
import { Slider } from "../components/ui/slider";
import PartnersWidget from "../components/PartnersWidget";

// CSV helper
const toCsv = (rows, columns) => {
  if (!rows?.length) return '';
  const header = columns.map(c => c.label).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const val = row[c.key];
      if (val === null || val === undefined) return '';
      const str = String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(',')
  ).join('\n');
  return header + '\n' + body;
};

const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const GlassCard = ({ children, className = "", span = "" }) => (
  <div className={`backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 ${span} ${className}`}>
    {children}
  </div>
);

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <GlassCard className="hover:bg-white/10 transition-all duration-300 hover:-translate-y-1">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">{title}</p>
        <p className="text-3xl font-bold text-white" data-testid={`stat-${title.toLowerCase().replace(/\s/g,'-')}`}>{value}</p>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </GlassCard>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Expandable Coach Card with linked athletes
const CoachCard = ({ coach }) => {
  const [expanded, setExpanded] = useState(false);
  const athleteCount = coach.athletes?.length || 0;

  return (
    <div className="rounded-xl bg-white/5 overflow-hidden" data-testid={`coach-card-${coach.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors"
        data-testid={`coach-toggle-${coach.id}`}
      >
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center shrink-0">
          <span className="text-white font-bold">{coach.name?.charAt(0)?.toUpperCase()}</span>
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="font-medium text-white truncate">{coach.name}</p>
          <p className="text-xs text-slate-400">{coach.specialty || coach.sport || 'Coach'} &middot; {athleteCount} athlete{athleteCount !== 1 ? 's' : ''}</p>
        </div>
        {athleteCount > 0 && (
          expanded
            ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" />
            : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && athleteCount > 0 && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 pl-1">Linked Athletes</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {coach.athletes.map(athlete => (
              <div key={athlete.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-bold">{athlete.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{athlete.name}</p>
                  {athlete.sport && <p className="text-xs text-slate-500">{athlete.sport}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ClubDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [periodData, setPeriodData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [periodLoading, setPeriodLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportType, setExportType] = useState('total');
  // Range slider: timeline position (0 = 730 days ago, 730 = today). Ascending for Radix.
  const [dateRange, setDateRange] = useState([460, 730]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  // Custom period slider (0 = 730 days ago, 730 = today)
  const [periodRange, setPeriodRange] = useState([640, 730]);
  // Overview filters
  const [filterAge, setFilterAge] = useState('all');
  const [filterDay, setFilterDay] = useState('all');
  const [filterTime, setFilterTime] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(true);

  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const clubRes = await axios.get(`${API}/clubs/by-admin/${user?.id}`);
        setClub(clubRes.data);
        const [membersRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/clubs/${clubRes.data.id}/members`),
          axios.get(`${API}/clubs/${clubRes.data.id}/analytics`)
        ]);
        setMembers(membersRes.data);
        setAnalytics(analyticsRes.data);
      } catch (e) {
        console.error("Error fetching club data:", e);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchClubData();
  }, [user?.id]);

  // Re-fetch analytics when filters change
  useEffect(() => {
    const refetchAnalytics = async () => {
      if (!club?.id) return;
      try {
        const fqs = buildFilterQS();
        const qs = fqs ? `?${fqs}` : '';
        const res = await axios.get(`${API}/clubs/${club.id}/analytics${qs}`);
        setAnalytics(res.data);
      } catch (e) {
        console.error("Error refetching analytics:", e);
      }
    };
    refetchAnalytics();
  }, [club?.id, filterAge, filterDay, filterTime]);

  // Fetch period analytics when period or tab changes
  useEffect(() => {
    const fetchPeriodData = async () => {
      if (!club?.id || activeTab !== 'my-period') return;
      setPeriodLoading(true);
      try {
        const fqs = buildFilterQS();
        let url = `${API}/clubs/${club.id}/period-analytics?period=${selectedPeriod}${fqs ? '&' + fqs : ''}`;
        if (selectedPeriod === 'other') {
          const now = new Date();
          const startDate = new Date(now);
          startDate.setDate(now.getDate() - (730 - periodRange[0]));
          const endDate = new Date(now);
          endDate.setDate(now.getDate() - (730 - periodRange[1]));
          const fmt = d => d.toISOString().split('T')[0];
          url = `${API}/clubs/${club.id}/period-analytics?period=other&start_date=${fmt(startDate)}&end_date=${fmt(endDate)}${fqs ? '&' + fqs : ''}`;
        }
        const res = await axios.get(url);
        setPeriodData(res.data);
      } catch (e) {
        console.error("Error fetching period data:", e);
      } finally {
        setPeriodLoading(false);
      }
    };
    fetchPeriodData();
  }, [club?.id, selectedPeriod, activeTab, periodRange, filterAge, filterDay, filterTime]);

  // Fetch weekly report when tab is active
  useEffect(() => {
    const fetchWeeklyReport = async () => {
      if (!club?.id || activeTab !== 'weekly-report') return;
      setWeeklyReportLoading(true);
      try {
        const fqs = buildFilterQS();
        const qs = fqs ? `?${fqs}` : '';
        const res = await axios.get(`${API}/clubs/${club.id}/weekly-report${qs}`);
        setWeeklyReport(res.data);
      } catch (e) {
        console.error("Error fetching weekly report:", e);
      } finally {
        setWeeklyReportLoading(false);
      }
    };
    fetchWeeklyReport();
  }, [club?.id, activeTab, filterAge, filterDay, filterTime]);

  // Fetch export data when Data tab is active or date range changes
  useEffect(() => {
    const fetchExportData = async () => {
      if (!club?.id || activeTab !== 'data') return;
      setExportLoading(true);
      try {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - (730 - dateRange[0]));
        const endDate = new Date(now);
        endDate.setDate(now.getDate() - (730 - dateRange[1]));
        const fmt = d => d.toISOString().split('T')[0];
        const res = await axios.get(`${API}/clubs/${club.id}/export-data?start_date=${fmt(startDate)}&end_date=${fmt(endDate)}${buildFilterQS() ? '&' + buildFilterQS() : ''}`);
        setExportData(res.data);
      } catch (e) {
        console.error("Error fetching export data:", e);
      } finally {
        setExportLoading(false);
      }
    };
    fetchExportData();
  }, [club?.id, dateRange, activeTab, filterAge, filterDay, filterTime]);

  const handleCopyCode = () => {
    if (club?.connect_code) {
      navigator.clipboard.writeText(club.connect_code);
      setCodeCopied(true);
      toast.success("Connect code copied!");
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleRegenerateCode = async () => {
    if (!club) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/clubs/${club.id}/regenerate-code?admin_id=${user?.id}`);
      setClub({ ...club, connect_code: res.data.connect_code });
      toast.success("New connect code generated!");
    } catch (e) {
      toast.error("Failed to regenerate code");
    } finally {
      setRegenerating(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/welcome'); };

  // Build filter query string
  const buildFilterQS = () => {
    const params = new URLSearchParams();
    if (filterAge !== 'all') params.set('age_group', filterAge);
    if (filterDay !== 'all') params.set('day_filter', filterDay);
    if (filterTime !== 'all') params.set('time_filter', filterTime);
    return params.toString();
  };

  // Date helpers for the range slider
  const daysAgoToDate = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d;
  };
  const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const rangeStartDate = daysAgoToDate(730 - dateRange[0]);
  const rangeEndDate = daysAgoToDate(730 - dateRange[1]);

  const handleExportCsv = (type) => {
    if (!exportData) return;
    const columnDefs = {
      total: [
        { key: 'athlete_id', label: 'Athlete ID' }, { key: 'age', label: 'Age' }, { key: 'gender', label: 'Gender' },
        { key: 'sport', label: 'Sport' }, { key: 'date', label: 'Date' }, { key: 'time', label: 'Time' },
        { key: 'day', label: 'Day' }, { key: 'training_type', label: 'Training Type' },
        { key: 'session_name', label: 'Session Name' }, { key: 'duration_min', label: 'Duration (min)' },
        { key: 'fun', label: 'Fun' }, { key: 'learning', label: 'Learning' },
        { key: 'mygoal', label: 'MyGoal' }, { key: 'intensity', label: 'Intensity' },
        { key: 'overall_avg', label: 'Overall Avg' }
      ],
      sessions: [
        { key: 'athlete_id', label: 'Athlete ID' }, { key: 'date', label: 'Date' }, { key: 'day', label: 'Day' },
        { key: 'time', label: 'Time' }, { key: 'training_type', label: 'Training Type' },
        { key: 'session_name', label: 'Session Name' }, { key: 'duration_min', label: 'Duration (min)' },
        { key: 'rated', label: 'Rated' }, { key: 'fun', label: 'Fun' }, { key: 'learning', label: 'Learning' },
        { key: 'mygoal', label: 'MyGoal' }, { key: 'intensity', label: 'Intensity' }
      ],
      goals: [
        { key: 'athlete_id', label: 'Athlete ID' }, { key: 'title', label: 'Goal' },
        { key: 'category', label: 'Category' }, { key: 'status', label: 'Status' },
        { key: 'reviews_count', label: 'Reviews' }, { key: 'avg_review_score', label: 'Avg Review Score' },
        { key: 'created_date', label: 'Created' }
      ],
      members: [
        { key: 'name', label: 'Name' }, { key: 'role', label: 'Role' }, { key: 'age', label: 'Age' },
        { key: 'gender', label: 'Gender' }, { key: 'sport', label: 'Sport' },
        { key: 'coaches', label: 'Coaches' }, { key: 'connected_date', label: 'Connected' }
      ]
    };
    const data = exportData[type] || [];
    const cols = columnDefs[type];
    const csv = toCsv(data, cols);
    const range = exportData.date_range;
    downloadCsv(csv, `${club?.name || 'club'}_${type}_${range.start}_to_${range.end}.csv`);
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported as CSV`);
  };

  // Derived data — all anonymous / aggregate only
  const weeklyActivity = analytics?.weekly_activity || [];
  const goalCompletion = analytics?.goal_stats?.completion_data || [];
  const avgRatings = analytics?.session_stats?.avg_ratings || {};
  const minRatings = analytics?.session_stats?.min_ratings || {};
  const maxRatings = analytics?.session_stats?.max_ratings || {};

  const ratingData = [
    { subject: 'Fun', value: avgRatings.fun || 0, min: minRatings.fun || 0, max: maxRatings.fun || 0, fullMark: 10 },
    { subject: 'Learning', value: avgRatings.learning || 0, min: minRatings.learning || 0, max: maxRatings.learning || 0, fullMark: 10 },
    { subject: 'MyGoal', value: avgRatings.mygoal || 0, min: minRatings.mygoal || 0, max: maxRatings.mygoal || 0, fullMark: 10 },
    { subject: 'Intensity', value: avgRatings.intensity || 0, min: minRatings.intensity || 0, max: maxRatings.intensity || 0, fullMark: 10 },
  ];

  const sessionsByType = analytics?.sessions_by_type || {};
  const sessionsByTypeDetailed = analytics?.sessions_by_type_detailed || {};
  const typeChartData = Object.entries(sessionsByType).map(([name, value]) => {
    const detail = sessionsByTypeDetailed[name] || {};
    return { name: name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()), value, avg: detail.avg || 0, min: detail.min || 0, max: detail.max || 0 };
  });

  const overallAvg = avgRatings.fun && avgRatings.learning && avgRatings.mygoal && avgRatings.intensity
    ? ((avgRatings.fun + avgRatings.learning + avgRatings.mygoal + avgRatings.intensity) / 4).toFixed(1)
    : '0';

  const coachesWithAthletes = members?.coaches_with_athletes || [];
  const unlinkedAthletes = members?.unlinked_athletes || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="page-club-dashboard">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{club?.name || 'Club Dashboard'}</h1><a href="/club-exercises" className="mt-1 inline-block text-sm text-violet-300 hover:underline">Club exercise library</a>
                <p className="text-sm text-slate-400">{club?.sport || 'Sports'} Organization</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {club?.connect_code && (
                <div className="hidden md:flex items-center backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl px-4 py-2">
                  <div>
                    <p className="text-xs text-slate-400 mb-0.5">Connect Code</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg tracking-widest text-white" data-testid="connect-code">{club.connect_code}</span>
                      <button onClick={handleCopyCode} className="p-1 hover:bg-white/10 rounded-lg transition-colors" data-testid="copy-code-btn">
                        {codeCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <button onClick={() => navigate('/profile')} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="profile-btn">
                <Settings className="w-5 h-5 text-slate-400" />
              </button>
              <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="logout-btn">
                <LogOut className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {['overview', 'weekly-report', 'my-period', 'analytics', 'data', 'members'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'my-period' ? 'My Period' : tab === 'weekly-report' ? 'Weekly Report' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 relative z-10">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading dashboard...</p>
          </div>
        ) : !club ? (
          <GlassCard className="text-center py-16">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-500" />
            <h3 className="text-xl font-bold text-white mb-2">No Club Found</h3>
            <p className="text-slate-400">Your club could not be loaded. Please try again.</p>
          </GlassCard>
        ) : (
          <>
            {/* Shared Filters — visible on all tabs except Members */}
            {activeTab !== 'members' && (
              <div className="mb-4">
                {/* Collapsed bar */}
                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all w-full sm:w-auto"
                  data-testid="toggle-filters"
                >
                  <Filter className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-sm font-medium text-white">Filters</span>
                  {(filterAge !== 'all' || filterDay !== 'all' || filterTime !== 'all') && (
                    <span className="px-1.5 py-0.5 rounded-full bg-purple-500/30 text-[10px] font-bold text-purple-300"
                      data-testid="active-filter-count"
                    >
                      {[filterAge, filterDay, filterTime].filter(f => f !== 'all').length}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded panel */}
                {filtersOpen && (
                  <div className="mt-2 p-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-xl animate-in slide-in-from-top-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <select value={filterAge} onChange={e => setFilterAge(e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-purple-500/50 cursor-pointer"
                        data-testid="filter-age">
                        <option value="all" className="bg-[#1a1a2e]">All Ages</option>
                        <option value="u12" className="bg-[#1a1a2e]">U12</option>
                        <option value="12-16" className="bg-[#1a1a2e]">12–16</option>
                        <option value="16-18" className="bg-[#1a1a2e]">16–18</option>
                        <option value="18-25" className="bg-[#1a1a2e]">18–25</option>
                        <option value="25+" className="bg-[#1a1a2e]">25+</option>
                      </select>
                      <select value={filterDay} onChange={e => setFilterDay(e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-purple-500/50 cursor-pointer"
                        data-testid="filter-day">
                        <option value="all" className="bg-[#1a1a2e]">All Days</option>
                        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                          <option key={d} value={d.toLowerCase()} className="bg-[#1a1a2e]">{d}</option>
                        ))}
                      </select>
                      <select value={filterTime} onChange={e => setFilterTime(e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-purple-500/50 cursor-pointer"
                        data-testid="filter-time">
                        <option value="all" className="bg-[#1a1a2e]">All Times</option>
                        <option value="morning" className="bg-[#1a1a2e]">Morning (6-12)</option>
                        <option value="afternoon" className="bg-[#1a1a2e]">Afternoon (12-17)</option>
                        <option value="evening" className="bg-[#1a1a2e]">Evening (17-22)</option>
                      </select>
                      {(filterAge !== 'all' || filterDay !== 'all' || filterTime !== 'all') && (
                        <button
                          onClick={() => { setFilterAge('all'); setFilterDay('all'); setFilterTime('all'); }}
                          className="text-xs text-purple-400 hover:text-purple-300 font-medium"
                          data-testid="clear-filters"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== OVERVIEW TAB ===== */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats — all aggregate, no individual athlete data */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Athletes" value={analytics?.members_stats?.total_athletes ?? 0} subtitle="connected" icon={Users} color="bg-gradient-to-br from-purple-600 to-purple-800" />
                  <StatCard title="Coaches" value={analytics?.members_stats?.total_coaches ?? 0} subtitle="active" icon={UserCheck} color="bg-gradient-to-br from-blue-600 to-blue-800" />
                  <StatCard title="Sessions" value={analytics?.session_stats?.sessions_this_week ?? 0} subtitle="this week" icon={Activity} color="bg-gradient-to-br from-emerald-600 to-emerald-800" />
                  <StatCard title="Avg Rating" value={overallAvg} subtitle="anonymous aggregate" icon={TrendingUp} color="bg-gradient-to-br from-orange-600 to-orange-800" />
                </div>

                {/* Charts — aggregate only */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <GlassCard span="lg:col-span-2">
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-white">Weekly Activity</h3>
                      <p className="text-sm text-slate-400">Sessions over the last 7 days</p>
                    </div>
                    {weeklyActivity.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={weeklyActivity}>
                          <defs>
                            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                          <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            return (
                              <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3 text-sm">
                                <p className="text-white font-semibold mb-1">{label}</p>
                                <p className="text-purple-400">Sessions: {d?.sessions}</p>
                                {d?.rating > 0 && (
                                  <>
                                    <p className="text-slate-300">Avg: {d?.rating}</p>
                                    <p className="text-emerald-400">Min: {d?.min_rating} / Max: {d?.max_rating}</p>
                                  </>
                                )}
                              </div>
                            );
                          }} />
                          <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#8b5cf6" strokeWidth={2} fill="url(#colorSessions)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[260px] flex items-center justify-center text-slate-500">No activity data yet</div>
                    )}
                  </GlassCard>

                  <GlassCard>
                    <h3 className="text-lg font-bold text-white mb-2">Goal Status</h3>
                    <p className="text-sm text-slate-400 mb-4">{analytics?.goal_stats?.total_goals ?? 0} total goals</p>
                    {goalCompletion.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={180}>
                          <RePieChart>
                            <Pie data={goalCompletion} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                              {goalCompletion.map((entry, index) => (
                                <Cell key={index} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </RePieChart>
                        </ResponsiveContainer>
                        <div className="flex justify-center gap-4 text-xs mt-2">
                          {goalCompletion.map((item, index) => (
                            <span key={index} className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
                              {item.name} ({item.value}%)
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="h-[180px] flex items-center justify-center text-slate-500">No goals yet</div>
                    )}
                  </GlassCard>
                </div>

                {/* Rating Range (Min / Avg / Max) */}
                <GlassCard>
                  <h3 className="text-lg font-bold text-white mb-1">Rating Range</h3>
                  <p className="text-sm text-slate-400 mb-5">Min, average, and max across all rated sessions (anonymous)</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                      { label: 'Fun', avg: avgRatings.fun || 0, min: minRatings.fun || 0, max: maxRatings.fun || 0, color: '#f59e0b', bg: 'bg-amber-500/10' },
                      { label: 'Learning', avg: avgRatings.learning || 0, min: minRatings.learning || 0, max: maxRatings.learning || 0, color: '#3b82f6', bg: 'bg-blue-500/10' },
                      { label: 'MyGoal', avg: avgRatings.mygoal || 0, min: minRatings.mygoal || 0, max: maxRatings.mygoal || 0, color: '#8b5cf6', bg: 'bg-purple-500/10' },
                      { label: 'Intensity', avg: avgRatings.intensity || 0, min: minRatings.intensity || 0, max: maxRatings.intensity || 0, color: '#ef4444', bg: 'bg-red-500/10' },
                    ].map(item => (
                      <div key={item.label} className={`p-4 rounded-xl ${item.bg} border border-white/5`} data-testid={`rating-range-${item.label.toLowerCase()}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-slate-300">{item.label}</span>
                          <span className="text-lg font-bold text-white">{item.avg}</span>
                        </div>
                        <div className="relative h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                          {/* Min-Max range bar */}
                          <div className="absolute h-full rounded-full opacity-40" style={{ left: `${item.min * 10}%`, width: `${(item.max - item.min) * 10}%`, backgroundColor: item.color }} />
                          {/* Avg marker */}
                          <div className="absolute h-full rounded-full" style={{ left: `${item.avg * 10 - 1}%`, width: '2%', backgroundColor: item.color }} />
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Min <span className="text-white font-semibold">{item.min}</span></span>
                          <span className="text-slate-500">Max <span className="text-white font-semibold">{item.max}</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>

                {/* Mobile Connect Code */}
                <GlassCard className="md:hidden">
                  <h3 className="text-lg font-bold text-white mb-3">Connect Code</h3>
                  <p className="text-sm text-slate-400 mb-3">Share this code with athletes and coaches to join your club</p>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-2xl tracking-widest text-white flex-1" data-testid="connect-code-mobile">{club?.connect_code}</span>
                    <button onClick={handleCopyCode} className="p-2 bg-white/10 rounded-lg" data-testid="copy-code-btn-mobile">
                      {codeCopied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5 text-slate-400" />}
                    </button>
                    <button onClick={handleRegenerateCode} disabled={regenerating} className="p-2 bg-white/10 rounded-lg">
                      <RefreshCw className={`w-5 h-5 text-slate-400 ${regenerating ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </GlassCard>

                {/* Anonymity notice */}
                <div className="flex items-center gap-3 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                    <Target className="w-4 h-4 text-purple-400" />
                  </div>
                  <p className="text-sm text-slate-300">All session ratings are <span className="text-white font-semibold">anonymous</span>. Only aggregate averages are displayed. Individual goals are visible per athlete.</p>
                </div>
              </div>
            )}

            {/* ===== WEEKLY REPORT TAB ===== */}
            {activeTab === 'weekly-report' && (
              <div className="space-y-6" data-testid="weekly-report-tab">
                {weeklyReportLoading ? (
                  <div className="text-center py-20">
                    <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Generating weekly report...</p>
                  </div>
                ) : !weeklyReport ? (
                  <GlassCard className="text-center py-16">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                    <h3 className="text-lg font-bold text-white mb-1">No report available</h3>
                    <p className="text-slate-400">Weekly report data could not be loaded.</p>
                  </GlassCard>
                ) : (
                  <>
                    {/* Report Header */}
                    <GlassCard className="border-purple-500/20">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#493986] to-[#92a7e8] flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white" data-testid="weekly-report-title">Weekly Report</h2>
                          <p className="text-sm text-slate-400">{weeklyReport.week_start} — {weeklyReport.week_end}</p>
                        </div>
                      </div>
                    </GlassCard>

                    {/* Key Metrics Row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <GlassCard className="hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Sessions</p>
                            <p className="text-3xl font-bold text-white" data-testid="wr-sessions-count">{weeklyReport.sessions.this_week}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {weeklyReport.sessions.trend.direction === 'up' ? (
                                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                              ) : weeklyReport.sessions.trend.direction === 'down' ? (
                                <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                              ) : (
                                <Minus className="w-3.5 h-3.5 text-slate-400" />
                              )}
                              <span className={`text-xs font-medium ${
                                weeklyReport.sessions.trend.direction === 'up' ? 'text-emerald-400' :
                                weeklyReport.sessions.trend.direction === 'down' ? 'text-red-400' : 'text-slate-400'
                              }`}>{weeklyReport.sessions.trend.diff} vs last week</span>
                            </div>
                          </div>
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
                            <Activity className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </GlassCard>

                      <GlassCard className="hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Avg Rating</p>
                            <p className="text-3xl font-bold text-white" data-testid="wr-overall-rating">{weeklyReport.ratings.overall.current}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {weeklyReport.ratings.overall.trend.direction === 'up' ? (
                                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                              ) : weeklyReport.ratings.overall.trend.direction === 'down' ? (
                                <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                              ) : (
                                <Minus className="w-3.5 h-3.5 text-slate-400" />
                              )}
                              <span className={`text-xs font-medium ${
                                weeklyReport.ratings.overall.trend.direction === 'up' ? 'text-emerald-400' :
                                weeklyReport.ratings.overall.trend.direction === 'down' ? 'text-red-400' : 'text-slate-400'
                              }`}>{weeklyReport.ratings.overall.trend.diff} vs last week</span>
                            </div>
                          </div>
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-600 to-orange-800 flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </GlassCard>

                      <GlassCard className="hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">New Members</p>
                            <p className="text-3xl font-bold text-white" data-testid="wr-new-members">{weeklyReport.new_members.count}</p>
                            <p className="text-xs text-slate-400 mt-1">joined this week</p>
                          </div>
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                            <UserPlus className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </GlassCard>

                      <GlassCard className="hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Goal Completion</p>
                            <p className="text-3xl font-bold text-white" data-testid="wr-goal-rate">{weeklyReport.goals.completion_rate}%</p>
                            <p className="text-xs text-slate-400 mt-1">{weeklyReport.goals.completed}/{weeklyReport.goals.total} completed</p>
                          </div>
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </GlassCard>
                    </div>

                    {/* Rating Trends */}
                    <GlassCard>
                      <h3 className="text-lg font-bold text-white mb-1">Rating Trends</h3>
                      <p className="text-sm text-slate-400 mb-5">This week vs last week (anonymous aggregates)</p>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          { key: 'fun', label: 'Fun', color: '#f59e0b', bg: 'bg-amber-500/10' },
                          { key: 'learning', label: 'Learning', color: '#3b82f6', bg: 'bg-blue-500/10' },
                          { key: 'mygoal', label: 'MyGoal', color: '#8b5cf6', bg: 'bg-purple-500/10' },
                          { key: 'intensity', label: 'Intensity', color: '#ef4444', bg: 'bg-red-500/10' },
                        ].map(item => {
                          const data = weeklyReport.ratings[item.key];
                          return (
                            <div key={item.key} className={`p-4 rounded-xl ${item.bg} border border-white/5`} data-testid={`wr-trend-${item.key}`}>
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-slate-300">{item.label}</span>
                                <div className="flex items-center gap-1">
                                  {data.trend.direction === 'up' ? (
                                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                                  ) : data.trend.direction === 'down' ? (
                                    <TrendingDown className="w-4 h-4 text-red-400" />
                                  ) : (
                                    <Minus className="w-4 h-4 text-slate-400" />
                                  )}
                                  <span className={`text-xs font-bold ${
                                    data.trend.direction === 'up' ? 'text-emerald-400' :
                                    data.trend.direction === 'down' ? 'text-red-400' : 'text-slate-400'
                                  }`}>{data.trend.diff}</span>
                                </div>
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-white">{data.current}</span>
                                <span className="text-xs text-slate-500">/ 10</span>
                              </div>
                              <div className="h-2 bg-white/10 rounded-full overflow-hidden mt-2">
                                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${data.current * 10}%`, backgroundColor: item.color }} />
                              </div>
                              <p className="text-xs text-slate-500 mt-2">Last week: {data.previous}</p>
                            </div>
                          );
                        })}
                      </div>
                    </GlassCard>

                    {/* Top Training Type + Training Types Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <GlassCard>
                        <div className="flex items-center gap-3 mb-4">
                          <Zap className="w-5 h-5 text-amber-400" />
                          <h3 className="text-lg font-bold text-white">Top Training Type</h3>
                        </div>
                        {weeklyReport.top_training_type ? (
                          <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
                            <p className="text-2xl font-bold text-white mb-1" data-testid="wr-top-type">{weeklyReport.top_training_type.name}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <div>
                                <p className="text-xs text-slate-400">Avg Rating</p>
                                <p className="text-lg font-bold text-amber-400">{weeklyReport.top_training_type.avg_rating}/10</p>
                              </div>
                              <div className="w-px h-8 bg-white/10" />
                              <div>
                                <p className="text-xs text-slate-400">Sessions</p>
                                <p className="text-lg font-bold text-white">{weeklyReport.top_training_type.session_count}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-slate-500">No rated sessions this week</p>
                          </div>
                        )}
                      </GlassCard>

                      <GlassCard>
                        <div className="flex items-center gap-3 mb-4">
                          <Activity className="w-5 h-5 text-purple-400" />
                          <h3 className="text-lg font-bold text-white">All Training Types</h3>
                        </div>
                        {weeklyReport.training_types_breakdown.length > 0 ? (
                          <div className="space-y-3">
                            {weeklyReport.training_types_breakdown.map((t, i) => (
                              <div key={t.name} className="flex items-center gap-3">
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white ${
                                  i === 0 ? 'bg-amber-500/30' : 'bg-white/10'
                                }`}>{i + 1}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-white truncate">{t.name}</span>
                                    <span className="text-sm font-bold text-white ml-2">{t.avg_rating}</span>
                                  </div>
                                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all duration-500" style={{
                                      width: `${t.avg_rating * 10}%`,
                                      backgroundColor: i === 0 ? '#f59e0b' : '#8b5cf6'
                                    }} />
                                  </div>
                                </div>
                                <span className="text-xs text-slate-500 shrink-0">{t.session_count} sessions</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-slate-500">No rated sessions this week</p>
                          </div>
                        )}
                      </GlassCard>
                    </div>

                    {/* Goal Progress */}
                    <GlassCard>
                      <div className="flex items-center gap-3 mb-5">
                        <Target className="w-5 h-5 text-emerald-400" />
                        <h3 className="text-lg font-bold text-white">Goal Progress</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="text-center p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                          <p className="text-2xl font-bold text-emerald-400" data-testid="wr-goals-completed">{weeklyReport.goals.completed}</p>
                          <p className="text-xs text-slate-400 mt-1">Completed</p>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                          <p className="text-2xl font-bold text-amber-400" data-testid="wr-goals-progress">{weeklyReport.goals.in_progress}</p>
                          <p className="text-xs text-slate-400 mt-1">In Progress</p>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
                          <p className="text-2xl font-bold text-slate-400" data-testid="wr-goals-not-started">{weeklyReport.goals.not_started}</p>
                          <p className="text-xs text-slate-400 mt-1">Not Started</p>
                        </div>
                      </div>
                      {weeklyReport.goals.total > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-slate-400">Overall completion</span>
                            <span className="text-sm font-bold text-white">{weeklyReport.goals.completion_rate}%</span>
                          </div>
                          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700" style={{ width: `${weeklyReport.goals.completion_rate}%` }} />
                          </div>
                        </div>
                      )}
                    </GlassCard>

                    {/* New Members List */}
                    {weeklyReport.new_members.count > 0 && (
                      <GlassCard>
                        <div className="flex items-center gap-3 mb-4">
                          <UserPlus className="w-5 h-5 text-blue-400" />
                          <h3 className="text-lg font-bold text-white">New Members This Week ({weeklyReport.new_members.count})</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {weeklyReport.new_members.list.map((m, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                              <div className="w-10 h-10 rounded-full bg-blue-600/30 flex items-center justify-center shrink-0">
                                <span className="text-white text-sm font-bold">{m.name?.charAt(0)?.toUpperCase()}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">{m.name}</p>
                                <p className="text-xs text-slate-500">{m.role} {m.sport ? `· ${m.sport}` : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </GlassCard>
                    )}

                    {/* Anonymity notice */}
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                        <Target className="w-4 h-4 text-purple-400" />
                      </div>
                      <p className="text-sm text-slate-300">All ratings in this report are <span className="text-white font-semibold">anonymous aggregates</span>. No individual athlete can be traced to their ratings.</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ===== MY PERIOD TAB ===== */}
            {activeTab === 'my-period' && (
              <div className="space-y-6">
                {/* Period Selector */}
                <GlassCard>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Select Period</h3>
                        <p className="text-sm text-slate-400">Compare category ratings vs MyGoal</p>
                      </div>
                    </div>
                    <div className="flex bg-white/5 rounded-xl p-1 border border-white/10" data-testid="period-selector">
                      {[
                        { value: 'week', label: 'Week' },
                        { value: 'month', label: 'Month' },
                        { value: 'year', label: 'Year' },
                        { value: 'other', label: 'Other' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setSelectedPeriod(opt.value)}
                          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedPeriod === opt.value
                              ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white shadow-lg'
                              : 'text-slate-400 hover:text-white'
                          }`}
                          data-testid={`period-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom date range slider for "Other" period */}
                  {selectedPeriod === 'other' && (
                    <div className="mt-5 pt-5 border-t border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-white">{formatDate(daysAgoToDate(730 - periodRange[0]))}</span>
                        <span className="text-xs text-slate-500">to</span>
                        <span className="text-sm font-medium text-white">{formatDate(daysAgoToDate(730 - periodRange[1]))}</span>
                      </div>
                      <Slider
                        value={periodRange}
                        onValueChange={(val) => setPeriodRange(val)}
                        min={0}
                        max={730}
                        step={1}
                        minStepsBetweenThumbs={7}
                        className="[&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-400 [&_[data-orientation=horizontal]>.relative]:bg-purple-500/30 [&_span[data-orientation=horizontal]]:bg-purple-500"
                        data-testid="period-range-slider"
                      />
                      <div className="flex justify-between mt-2">
                        <span className="text-xs text-slate-500">2 years ago</span>
                        <span className="text-xs text-slate-500">Today</span>
                      </div>
                    </div>
                  )}
                </GlassCard>

                {periodLoading ? (
                  <div className="text-center py-16">
                    <div className="w-10 h-10 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-slate-400">Loading period data...</p>
                  </div>
                ) : !periodData?.categories?.length ? (
                  <GlassCard className="text-center py-16">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                    <h3 className="text-lg font-bold text-white mb-1">No rated sessions</h3>
                    <p className="text-slate-400">No rated training sessions found for this period.</p>
                  </GlassCard>
                ) : (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <GlassCard>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Total Sessions</p>
                        <p className="text-2xl font-bold text-white mt-1" data-testid="period-total-sessions">{periodData.total_sessions}</p>
                      </GlassCard>
                      <GlassCard>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Rated Sessions</p>
                        <p className="text-2xl font-bold text-white mt-1" data-testid="period-rated-sessions">{periodData.total_rated}</p>
                      </GlassCard>
                      <GlassCard className="col-span-2 lg:col-span-1">
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Categories</p>
                        <p className="text-2xl font-bold text-white mt-1">{periodData.categories.length}</p>
                      </GlassCard>
                    </div>

                    {/* Main comparison chart */}
                    <GlassCard>
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-white">Category Avg vs MyGoal</h3>
                        <p className="text-sm text-slate-400">Anonymous average rating per training category compared to MyGoal rating</p>
                      </div>
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart data={periodData.categories} barGap={4} barCategoryGap="20%">
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                          <XAxis dataKey="category" stroke="#64748b" fontSize={12} tick={{ fill: '#94a3b8' }} />
                          <YAxis domain={[0, 10]} stroke="#64748b" fontSize={12} tick={{ fill: '#64748b' }} />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3 text-sm">
                                  <p className="text-white font-semibold mb-1">{label}</p>
                                  {payload.map((e, i) => (
                                    <p key={i} style={{ color: e.color }}>{e.name}: {e.value}</p>
                                  ))}
                                  <p className="text-slate-400 mt-1">{periodData.categories.find(c => c.category === label)?.sessions} sessions</p>
                                </div>
                              );
                            }}
                          />
                          <Legend
                            wrapperStyle={{ paddingTop: 16 }}
                            formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>}
                          />
                          <Bar dataKey="avg_overall" name="Average Rating" radius={[4, 4, 0, 0]} fill="#8b5cf6" />
                          <Bar dataKey="avg_mygoal" name="MyGoal Rating" radius={[4, 4, 0, 0]} fill="#f59e0b" />
                        </BarChart>
                      </ResponsiveContainer>
                    </GlassCard>

                    {/* Detailed breakdown per category */}
                    <GlassCard>
                      <h3 className="text-lg font-bold text-white mb-4">Detailed Breakdown</h3>
                      <div className="space-y-5">
                        {periodData.categories.map(cat => {
                          const diff = (cat.avg_mygoal - cat.avg_overall).toFixed(1);
                          const isPositive = diff > 0;
                          return (
                            <div key={cat.category} className="p-4 rounded-xl bg-white/5" data-testid={`period-cat-${cat.category}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <p className="font-medium text-white">{cat.category}</p>
                                  <p className="text-xs text-slate-400">{cat.sessions} rated sessions</p>
                                </div>
                                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  isPositive ? 'bg-emerald-500/20 text-emerald-400' : diff < 0 ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-slate-400'
                                }`}>
                                  {isPositive ? '+' : ''}{diff} MyGoal gap
                                </div>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                {[
                                  { label: 'Overall', value: cat.avg_overall, color: '#8b5cf6' },
                                  { label: 'MyGoal', value: cat.avg_mygoal, color: '#f59e0b' },
                                  { label: 'Fun', value: cat.avg_fun, color: '#10b981' },
                                  { label: 'Learning', value: cat.avg_learning, color: '#3b82f6' },
                                  { label: 'Intensity', value: cat.avg_intensity, color: '#ef4444' },
                                ].map(item => (
                                  <div key={item.label}>
                                    <div className="flex justify-between mb-1">
                                      <span className="text-xs text-slate-500">{item.label}</span>
                                      <span className="text-xs font-bold text-white">{item.value}</span>
                                    </div>
                                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${item.value * 10}%`, backgroundColor: item.color }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </GlassCard>
                  </>
                )}
              </div>
            )}

            {/* ===== ANALYTICS TAB ===== */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <GlassCard>
                    <h3 className="text-lg font-bold text-white mb-2">Rating Overview</h3>
                    <p className="text-sm text-slate-400 mb-4">Anonymous averages across all members</p>
                    {avgRatings.fun || avgRatings.learning || avgRatings.mygoal || avgRatings.intensity ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <RadarChart data={ratingData}>
                          <PolarGrid stroke="#ffffff20" />
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                          <PolarRadiusAxis domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 10 }} />
                          <Radar name="Max" dataKey="max" stroke="#8b5cf620" fill="#8b5cf6" fillOpacity={0.1} strokeDasharray="4 4" />
                          <Radar name="Average" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.35} />
                          <Radar name="Min" dataKey="min" stroke="#ef444460" fill="#ef4444" fillOpacity={0.1} strokeDasharray="4 4" />
                          <Tooltip content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            return (
                              <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3 text-sm">
                                <p className="text-white font-semibold mb-1">{d?.subject}</p>
                                <p className="text-purple-400">Avg: {d?.value}</p>
                                <p className="text-emerald-400">Min: {d?.min} / Max: {d?.max}</p>
                              </div>
                            );
                          }} />
                          <Legend formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No rating data yet</div>
                    )}
                  </GlassCard>

                  <GlassCard>
                    <h3 className="text-lg font-bold text-white mb-2">Sessions by Type</h3>
                    <p className="text-sm text-slate-400 mb-4">{analytics?.session_stats?.total_sessions ?? 0} total sessions</p>
                    {typeChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={typeChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                          <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            return (
                              <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3 text-sm">
                                <p className="text-white font-semibold mb-1">{label}</p>
                                <p className="text-purple-400">Sessions: {d?.value}</p>
                                {d?.avg > 0 && (
                                  <>
                                    <p className="text-slate-300">Avg Rating: {d?.avg}</p>
                                    <p className="text-emerald-400">Min: {d?.min} / Max: {d?.max}</p>
                                  </>
                                )}
                              </div>
                            );
                          }} />
                          <Bar dataKey="value" name="Sessions" radius={[4, 4, 0, 0]} fill="#8b5cf6" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No session data yet</div>
                    )}
                  </GlassCard>
                </div>

                <GlassCard>
                  <h3 className="text-lg font-bold text-white mb-4">Rating Breakdown</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { label: 'Fun', value: avgRatings.fun || 0, min: minRatings.fun || 0, max: maxRatings.fun || 0, color: '#f59e0b' },
                      { label: 'Learning', value: avgRatings.learning || 0, min: minRatings.learning || 0, max: maxRatings.learning || 0, color: '#3b82f6' },
                      { label: 'MyGoal', value: avgRatings.mygoal || 0, min: minRatings.mygoal || 0, max: maxRatings.mygoal || 0, color: '#8b5cf6' },
                      { label: 'Intensity', value: avgRatings.intensity || 0, min: minRatings.intensity || 0, max: maxRatings.intensity || 0, color: '#ef4444' },
                    ].map(item => (
                      <div key={item.label}>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-slate-400">{item.label}</span>
                          <span className="text-sm font-bold text-white">{item.value}/10</span>
                        </div>
                        <div className="relative h-3 bg-white/10 rounded-full overflow-hidden">
                          <div className="absolute h-full rounded-full opacity-30" style={{ left: `${item.min * 10}%`, width: `${(item.max - item.min) * 10}%`, backgroundColor: item.color }} />
                          <div className="absolute h-full rounded-full" style={{ width: `${item.value * 10}%`, backgroundColor: item.color }} />
                        </div>
                        <div className="flex justify-between mt-1.5 text-xs text-slate-500">
                          <span>Min {item.min}</span>
                          <span>Max {item.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <GlassCard>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Total Sessions</p>
                    <p className="text-2xl font-bold text-white mt-1">{analytics?.session_stats?.total_sessions ?? 0}</p>
                  </GlassCard>
                  <GlassCard>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Rated Sessions</p>
                    <p className="text-2xl font-bold text-white mt-1">{analytics?.session_stats?.total_rated ?? 0}</p>
                  </GlassCard>
                  <GlassCard>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Goals Completed</p>
                    <p className="text-2xl font-bold text-emerald-400 mt-1">{analytics?.goal_stats?.goals_completed ?? 0}</p>
                  </GlassCard>
                  <GlassCard>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Goals In Progress</p>
                    <p className="text-2xl font-bold text-yellow-400 mt-1">{analytics?.goal_stats?.goals_in_progress ?? 0}</p>
                  </GlassCard>
                </div>
              </div>
            )}

            {/* ===== DATA EXPORT TAB ===== */}
            {activeTab === 'data' && (
              <div className="space-y-6">
                {/* Date Range Selector */}
                <GlassCard>
                  <div className="flex items-center gap-3 mb-4">
                    <Database className="w-5 h-5 text-purple-400" />
                    <div>
                      <h3 className="text-lg font-bold text-white">Export Club Data</h3>
                      <p className="text-sm text-slate-400">Select a date range and download as CSV</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-white" data-testid="range-start">{formatDate(rangeStartDate)}</span>
                      <span className="text-xs text-slate-500">to</span>
                      <span className="text-sm font-medium text-white" data-testid="range-end">{formatDate(rangeEndDate)}</span>
                    </div>
                    <Slider
                      value={dateRange}
                      onValueChange={(val) => setDateRange(val)}
                      min={0}
                      max={730}
                      step={1}
                      minStepsBetweenThumbs={7}
                      className="[&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-400 [&_[data-orientation=horizontal]>.relative]:bg-purple-500/30 [&_span[data-orientation=horizontal]]:bg-purple-500"
                      data-testid="date-range-slider"
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-slate-500">2 years ago</span>
                      <span className="text-xs text-slate-500">Today</span>
                    </div>
                  </div>
                  {/* Quick presets */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Last 7 days', range: [723, 730] },
                      { label: 'Last 30 days', range: [700, 730] },
                      { label: 'Last 3 months', range: [640, 730] },
                      { label: 'Last 6 months', range: [550, 730] },
                      { label: 'Last year', range: [365, 730] },
                    ].map(preset => (
                      <button
                        key={preset.label}
                        onClick={() => setDateRange(preset.range)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                          dateRange[0] === preset.range[0] && dateRange[1] === preset.range[1]
                            ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                            : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                        }`}
                        data-testid={`preset-${preset.label.replace(/\s/g, '-').toLowerCase()}`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </GlassCard>

                {exportLoading ? (
                  <div className="text-center py-12">
                    <div className="w-10 h-10 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-slate-400">Loading data...</p>
                  </div>
                ) : exportData ? (
                  <>
                    {/* Data type selector + totals */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { type: 'total', icon: FileSpreadsheet, label: 'Total', sublabel: 'One row per filled-in rating with all data', count: exportData.totals.total_rows },
                        { type: 'sessions', icon: Activity, label: 'All Sessions', sublabel: 'Every session per athlete (rated + unrated)', count: exportData.totals.sessions_rows },
                        { type: 'goals', icon: Target, label: 'Goals', sublabel: 'Per athlete goals & reviews', count: exportData.totals.goals_rows },
                        { type: 'members', icon: Users, label: 'Members', sublabel: 'All connected members', count: exportData.totals.members_rows },
                      ].map(item => (
                        <button
                          key={item.type}
                          onClick={() => setExportType(item.type)}
                          className={`p-4 rounded-2xl border text-left transition-all ${
                            exportType === item.type
                              ? 'bg-purple-500/10 border-purple-500/40'
                              : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                          data-testid={`export-type-${item.type}`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <item.icon className={`w-5 h-5 ${exportType === item.type ? 'text-purple-400' : 'text-slate-400'}`} />
                            <span className="font-semibold text-white">{item.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 mb-2">{item.sublabel}</p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-white">{item.count}</span>
                            <span className="text-xs text-slate-400">rows</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Data Preview Table */}
                    <GlassCard>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Table2 className="w-5 h-5 text-purple-400" />
                          <div>
                            <h3 className="text-lg font-bold text-white">Preview: {exportType.charAt(0).toUpperCase() + exportType.slice(1)}</h3>
                            <p className="text-xs text-slate-400">Showing first 10 rows</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleExportCsv(exportType)}
                          disabled={!exportData[exportType]?.length}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-40"
                          data-testid="download-csv-btn"
                        >
                          <Download className="w-4 h-4" />
                          Download CSV
                        </button>
                      </div>
                      
                      {exportData[exportType]?.length > 0 ? (
                        <div className="overflow-x-auto -mx-6 px-6">
                          <table className="w-full text-sm" data-testid="data-preview-table">
                            <thead>
                              <tr className="border-b border-white/10">
                                {Object.keys(exportData[exportType][0]).map(key => (
                                  <th key={key} className="py-2 px-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">
                                    {key.replace(/_/g, ' ')}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {exportData[exportType].slice(0, 10).map((row, i) => (
                                <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                  {Object.values(row).map((val, j) => (
                                    <td key={j} className="py-2.5 px-3 text-slate-300 whitespace-nowrap">
                                      {val === null || val === undefined ? <span className="text-slate-600">—</span> : String(val)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {exportData[exportType].length > 10 && (
                            <p className="text-xs text-slate-500 mt-3 text-center">
                              Showing 10 of {exportData[exportType].length} rows. Download CSV for full data.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                          <p className="text-slate-400">No data for this range</p>
                        </div>
                      )}
                    </GlassCard>

                    {/* Export all */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => {
                          handleExportCsv('total');
                          handleExportCsv('sessions');
                          handleExportCsv('goals');
                          handleExportCsv('members');
                        }}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 transition-all text-sm"
                        data-testid="download-all-btn"
                      >
                        <Download className="w-4 h-4" />
                        Export All (4 files)
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {/* ===== MEMBERS TAB ===== */}
            {activeTab === 'members' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Club Members</h2>
                    <p className="text-slate-400">{members?.total_members || 0} total members connected</p>
                  </div>
                  <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl px-4 py-2">
                    <p className="text-xs text-slate-400">Connect Code</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold tracking-widest text-white">{club?.connect_code}</span>
                      <button onClick={handleCopyCode} className="p-1 hover:bg-white/10 rounded transition-colors">
                        {codeCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                      </button>
                      <button onClick={handleRegenerateCode} disabled={regenerating} className="p-1 hover:bg-white/10 rounded transition-colors" data-testid="regenerate-code-btn">
                        <RefreshCw className={`w-4 h-4 text-slate-400 ${regenerating ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Coaches with expandable athlete lists */}
                <GlassCard>
                  <div className="flex items-center gap-3 mb-4">
                    <UserCheck className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-bold text-white">Coaches ({members?.coaches_count || 0})</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">Tap a coach to see their linked athletes</p>
                  {coachesWithAthletes.length > 0 ? (
                    <div className="space-y-3">
                      {coachesWithAthletes.map(coach => (
                        <CoachCard key={coach.id} coach={coach} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">No coaches connected yet. Share the connect code to invite coaches.</p>
                  )}
                </GlassCard>

                {/* Unlinked Athletes */}
                {unlinkedAthletes.length > 0 && (
                  <GlassCard>
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="w-5 h-5 text-blue-400" />
                      <h3 className="text-lg font-bold text-white">Athletes without a coach ({unlinkedAthletes.length})</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {unlinkedAthletes.map(athlete => (
                        <div key={athlete.id} className="flex items-center gap-2 p-3 rounded-xl bg-white/5">
                          <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center shrink-0">
                            <span className="text-white text-xs font-bold">{athlete.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{athlete.name}</p>
                            {athlete.sport && <p className="text-xs text-slate-500">{athlete.sport}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                )}

                {/* All Athletes count summary */}
                <GlassCard>
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-bold text-white">Total Athletes ({members?.athletes_count || 0})</h3>
                  </div>
                  <p className="text-sm text-slate-400">All athletes connected to the club. Ratings remain anonymous — no individual rating data is shown here.</p>
                </GlassCard>
              </div>
            )}
          </>
        )}

        {/* Partners widget — bottom of dashboard */}
        {club && <PartnersWidget userId={user?.id} className="mt-8" />}
      </main>
    </div>
  );
};

export default ClubDashboardPage;
